/**
 * «Боты» (`/bots`, право `chat_groups`): наши боты Telegram и MAX — в каких группах состоят, права
 * бота, сколько сообщений группы мы увидели вчера / сегодня / за 7 дней, когда последнее и дошла ли
 * группа до «Чатов». Сверху — группы с бедой. Ниже — группы, которые сотрудники открывали в браузере,
 * а в «Чатах» их нет. Числа — те же, что у `prokat-runner bots`. Спека `docs/specs/2026-10-21-bots-design.md`.
 */
import { useGetIdentity } from "@refinedev/core";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";

import { NoAccess } from "@/components/NoAccess";
import { CHAT_GROUPS, has } from "@/lib/access";
import { type Bot, type BotGroup, type BrowserGroup, loadBots } from "@/lib/bots";
import type { CurrentUser } from "@/lib/providers";
import { card, td, th } from "@/pages/AppointmentsUi";

const CHANNEL: Record<string, string> = { telegram: "Telegram", max: "MAX", whatsapp: "WhatsApp" };

/** `2026-10-20T16:32:08` → «20.10 16:32»; сегодняшнее — только время. */
function when(iso: string | null, today: string): string {
  if (!iso) return "—";
  const t = iso.slice(11, 16);
  return iso.slice(0, 10) === today ? t : `${iso.slice(8, 10)}.${iso.slice(5, 7)} ${t}`;
}

const n = (v: number) => (v ? v : <span className="text-fg-faint">·</span>);

function Groups({ bot, today }: { bot: Bot; today: string }) {
  const bad = bot.groups.filter((g) => g.problem).length;
  const max = bot.code === "max";
  return (
    <div className="overflow-x-auto border-t border-border">
      <table className="w-full text-[12.5px]">
        <thead className="text-[11.5px] text-fg-faint">
          <tr>
            <th className="px-4 py-1.5 text-left font-medium">Группа · {bot.groups.length}{bad ? ` · с бедой ${bad}` : ""}</th>
            <th className="px-3 py-1.5 text-left font-medium">Бот в группе</th>
            {max && <th className={th}>Участников</th>}
            <th className={th}>Вчера</th>
            <th className={th}>Сегодня</th>
            <th className={th}>7 дней</th>
            <th className={th}>Последнее у нас</th>
            {max && <th className={th} title="самое свежее сообщение группы, которое отдаёт API бота">У бота MAX</th>}
            <th className="px-3 py-1.5 text-left font-medium">В «Чатах»</th>
            <th className="px-3 py-1.5 text-left font-medium">Что не так</th>
          </tr>
        </thead>
        <tbody>
          {bot.groups.map((g) => (
            <Row key={g.id} g={g} max={max} today={today} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Row({ g, max, today }: { g: BotGroup; max: boolean; today: string }) {
  const ok = g.status === "administrator" || g.status === "creator";
  return (
    <tr className={`border-t border-border hover:bg-bg-hover ${g.problem ? "bg-err/5" : ""}`} data-annot={`группа ${g.title}`}>
      <td className="px-4 py-1.5">
        <span className="font-medium">{g.title}</span>
        <span className="ml-1.5 font-mono text-[10.5px] text-fg-faint">{g.id}</span>
      </td>
      <td className={`whitespace-nowrap px-3 py-1.5 ${ok ? "text-fg-muted" : "text-err"}`}>{g.status_label}</td>
      {max && <td className={`${td} text-fg-muted`}>{g.members ?? "—"}</td>}
      <td className={td}>{n(g.yesterday)}</td>
      <td className={td}>{n(g.today)}</td>
      <td className={td}>{n(g.week)}</td>
      <td className={`${td} whitespace-nowrap text-fg-muted`}>{when(g.last, today)}</td>
      {max && (
        <td className={`${td} whitespace-nowrap text-fg-muted`} title={g.platform_event ? `последнее событие группы ${when(g.platform_event, today)}` : undefined}>
          {when(g.platform_last, today)}
        </td>
      )}
      <td className="whitespace-nowrap px-3 py-1.5">
        {g.chat ? (
          <Link to={`/chats/${g.chat.id}`} className="text-accent hover:underline" title={`в «Чатах» за 7 дней ${g.chat.week}`}>
            чат {g.chat.id}
          </Link>
        ) : (
          <span className="text-err">нет</span>
        )}
      </td>
      <td className={`px-3 py-1.5 ${g.problem ? "text-err" : "text-fg-faint"}`}>{g.problem ?? "—"}</td>
    </tr>
  );
}

function BotCard({ bot, today }: { bot: Bot; today: string }) {
  const p = bot.private;
  return (
    <section className={card} data-annot={`бот ${bot.username}`}>
      <div className="flex flex-col gap-1.5 px-4 py-3">
        <div className="flex flex-wrap items-baseline gap-x-3">
          <h2 className="text-[13.5px] font-medium">{bot.title}</h2>
          <span className="font-mono text-[12px] text-fg-muted">@{bot.username}</span>
          {bot.read_all !== null && (
            <span className={`text-[12px] ${bot.read_all ? "text-fg-faint" : "text-err"}`}>
              {bot.read_all ? "видит все сообщения групп" : "режим приватности: в группах видит только обращения к нему"}
            </span>
          )}
          {bot.error && <span className="text-[12px] text-err">{bot.error}</span>}
        </div>
        <p className="text-[12px] text-fg-faint">{bot.how}</p>
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-[12.5px]">
          <span>
            <span className="text-fg-muted">Личка за 7 дней:</span> {p.chats} диалогов, {p.messages} сообщений · последнее {when(p.last, today)}
          </span>
          {bot.kinds.length > 0 && (
            <span>
              <span className="text-fg-muted">Сообщения групп за 7 дней:</span> {bot.kinds.map((k) => `${k.label} ${k.n}`).join(", ")}
            </span>
          )}
        </div>
      </div>
      {bot.groups.length > 0 && <Groups bot={bot} today={today} />}
    </section>
  );
}

function Browser({ rows, days, today }: { rows: BrowserGroup[]; days: number; today: string }) {
  return (
    <section className={card} data-annot="группы из браузера без «Чатов»">
      <div className="flex flex-col gap-1 border-b border-border px-4 py-3">
        <h2 className="text-[13.5px] font-medium">Открывали в браузере, а в «Чатах» нет · {rows.length}</h2>
        <p className="text-[12px] text-fg-faint">
          Группы, которые сотрудники открывали в Telegram Web, MAX или WhatsApp Web с расширением за {days} дней. Из браузера в «Чаты» идут только
          группы рабочих номеров; группу, где есть наш бот, ведёт бот.
        </p>
      </div>
      {rows.length === 0 ? (
        <p className="px-4 py-3 text-[12.5px] text-fg-muted">Таких нет — все группы, что открывали, есть в «Чатах».</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead className="text-[11.5px] text-fg-faint">
              <tr>
                <th className="px-4 py-1.5 text-left font-medium">Группа</th>
                <th className="px-3 py-1.5 text-left font-medium">Канал</th>
                <th className={th}>Событий вкладки</th>
                <th className={th}>Отправок</th>
                <th className={th}>Последнее</th>
                <th className="px-3 py-1.5 text-left font-medium">Кто открывал</th>
                <th className="px-3 py-1.5 text-left font-medium">Почему нет</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((x) => (
                <tr key={`${x.channel}:${x.peer}`} className="border-t border-border hover:bg-bg-hover">
                  <td className="px-4 py-1.5">
                    {x.name || <span className="text-fg-faint">без названия</span>}
                    <span className="ml-1.5 font-mono text-[10.5px] text-fg-faint">{x.peer}</span>
                  </td>
                  <td className="px-3 py-1.5 text-fg-muted">{CHANNEL[x.channel] ?? x.channel}</td>
                  <td className={td}>{x.events}</td>
                  <td className={td}>{n(x.sends)}</td>
                  <td className={`${td} whitespace-nowrap text-fg-muted`}>{when(x.last, today)}</td>
                  <td className="px-3 py-1.5">
                    {x.users}
                    {x.accounts && <span className="text-fg-faint"> · с номера {x.accounts}</span>}
                  </td>
                  <td className="px-3 py-1.5 text-fg-muted">{x.why}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function BotsPage() {
  const { data: me } = useGetIdentity<CurrentUser>();
  // Без права ручка отвечает 403 — не спрашиваем, пока личность не приехала и право не подтверждено.
  const q = useQuery({ queryKey: ["bots"], queryFn: loadBots, enabled: has(me, CHAT_GROUPS) });
  if (me && !has(me, CHAT_GROUPS)) return <NoAccess what="разделу «Боты»" />;
  const d = q.data;
  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
      <div className="flex flex-wrap items-baseline gap-3" data-annot="шапка ботов">
        <h1 className="text-[16px] font-semibold tracking-[-0.015em]">Боты</h1>
        <span className="text-[12px] text-fg-faint">
          в каких группах наши боты, сколько сообщений мы из них увидели и дошли ли они до «Чатов»; сверху — группы с бедой
        </span>
        <button type="button" onClick={() => void q.refetch()} disabled={q.isFetching} className="ml-auto rounded-[var(--radius-ui)] border border-border px-2.5 py-1 text-[12px] text-fg-muted hover:text-fg disabled:opacity-50">
          {q.isFetching ? "Проверяю…" : "Проверить заново"}
        </button>
      </div>
      {q.isPending && <p className="text-[12.5px] text-fg-muted">Спрашиваю ботов…</p>}
      {q.error && <p className="text-[12.5px] text-err">{String(q.error)}</p>}
      {d && (
        <>
          {d.bots.map((b) => (
            <BotCard key={b.code} bot={b} today={d.today} />
          ))}
          <Browser rows={d.browser} days={d.days} today={d.today} />
        </>
      )}
    </main>
  );
}
